import { getAuthHeaders, tempMeeApi } from '~/services/api';
import {
  ISaltclickIdentityTokenEnvelope,
  ISaltclickIdentityTokenResponse,
} from '../types/SaltclickChat';

const IDENTITY_TOKEN_PATH = '/auth-service/saltclick/identity-token';

/**
 * Mints the short-lived RS256 JWT that Salesforce Embedded Messaging verifies the
 * chat session against. Lives in auth-service, which already owns token issuance,
 * so there is no endpoint to configure — it rides the app's own API host and
 * access token. GOTU-6364.
 *
 * There is no request body, by design: the endpoint derives the user from the
 * access token, so a client cannot ask for anyone else's token. `sub` in the
 * response is therefore the caller's own id — which is what makes it worth
 * checking against the user the caller believes is logged in.
 *
 * The token deliberately carries no PII (auth-service dropped the email and name
 * claims on a data-minimization review — a JWT payload is only base64url, so
 * anything in it travels readable into Salesforce's logs and retention).
 * Salesforce resolves the profile from `gtAdminPanelId`.
 *
 * Two failures leave the session anonymous rather than break the widget: 503
 * until the signing key is provisioned in an environment, and 400 for an account
 * whose `userType` Salesforce has no mapping for (`unassigned`, `ADM`).
 */
export const fetchSaltclickIdentityToken = async (): Promise<ISaltclickIdentityTokenResponse> => {
  const headers = await getAuthHeaders();
  const { data: envelope } = await tempMeeApi.post<ISaltclickIdentityTokenEnvelope>(
    IDENTITY_TOKEN_PATH,
    undefined,
    { headers },
  );

  const identityToken = envelope?.data;
  if (!identityToken?.token) {
    throw new Error('[Saltclick] identity token response contained no token');
  }

  return identityToken;
};
